<template>
  <ul class="child-menu">
    <li
      class="child-menu__list-item"
      v-for="menuItem in menuItems"
      :key="`${menuItem.uri}-${menuItem.title}`"
      :class="listItemClass(menuItem)"
      @mouseenter="!isTouchDevice && hasChildren(menuItem) && (openChildMenu = menuItem.id)"
      @mouseleave="!isTouchDevice && (openChildMenu = null)">
      <template v-if="hasChildren(menuItem)">
        <button class="child-menu__list-link" @click="toggleChild(menuItem)">
        <span v-html="menuItem.title" />
        <icon-angle-right class="child-menu__list-link-icon" />
      </button>
      <child-menu
        v-show="openChildMenu === menuItem.id"
        class="child-menu__child-menu"
        :menu-items="menuItem.children"
        @linkClicked="$emit('linkClicked')" />
      </template>
      <nuxt-link
        v-else
        class="child-menu__list-link"
        :to="menuItem.uri"
        :target="menuItem.inNewTab ? '_blank' : '_self'"
        @click="$emit('linkClicked')">
        <span v-html="menuItem.title" />
      </nuxt-link>
    </li>
  </ul>
</template>

<script lang="ts">
import IconAngleRight from '~/assets/icons/IconAngleRight.svg'
import type {MenuNodeResponse} from "~/stores/types/menu";

export default {
  name: 'ChildMenu',
  components: {
    IconAngleRight,
  },
  props: {
    menuItems: Object as Record<number, MenuNodeResponse>[],
  },
  data () {
    return {
      openChildMenu: null,
      isTouchDevice: false,
    };
  },
  mounted() {
    this.checkTouchDevice();
    window.addEventListener('resize', this.checkTouchDevice);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkTouchDevice);
  },
  methods: {
    checkTouchDevice() {
      // Mobile menu mode below 1440px - disable hover behavior
      this.isTouchDevice = window.innerWidth < 1440;
    },
    hasChildren (menuItem) {
      return menuItem.children.length > 0;
    },
    toggleChild (menuItem) {
      if (this.openChildMenu === menuItem.id) {
        this.openChildMenu = null;
      } else {
        this.openChildMenu = menuItem.id;
      }
    },
    listItemClass (menuItem) {
      return {
        'child-menu__list-item--open': this.openChildMenu === menuItem.id,
      };
    },
  },
  emits: ['linkClicked'],
};
</script>

<style lang="scss">
@use "~/common/scss/variables";
@use "~/common/scss/helpers";
@use "~/common/scss/mq";

.child-menu {
  $root: &;

  display: flex;
  flex-flow: column;
  list-style: none;
  padding: 0 0;

  &__list-item {
    position: relative;
    width: helpers.px-to-rem(250);

    @include mq.mq(variables.$mq-4-max) {
      margin: 0;
      width: 100%;

      &:not(:last-child) {
        border-bottom: 1px solid variables.$colorGreyBorder;
      }
    }

    .child-menu__list-link {
      background-color: #f4f4f4;
      &:hover {
        background-color: #d8d8d8;
      }
    }

  }

  &__list-link {
    align-items: center;
    color: #000;
    display: flex;
    flex: 0 0 100%;
    height: max-content;
    justify-content: space-between;
    min-height: helpers.px-to-rem(38);
    padding: .25rem .5rem;
    text-align: left;
    text-decoration: none;
    transition: background-color .25s;
    width: 100%;

    &:hover {
      background-color: #D8D8D8;
    }

    &.router-link-exact {
      background-color: #ffff00;
    }

    @include mq.mq(variables.$mq-4-max) {
      padding: helpers.px-to-rem(10) helpers.px-to-rem(20);
    }
  }

  &__list-link-icon {
    @include mq.mq(variables.$mq-4-max) {
      transform: rotate(90deg);
      transition: transform .25s;

      #{$root}__list-item--open > #{$root}__list-link & {
        transform: rotate(270deg);
      }
    }
  }

  &__child-menu {
    display: flex;
    background-color: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1000;

    @include mq.mq(variables.$mq-4-max) {
      padding-left: helpers.px-to-rem(20);
      background-color: #f9f9f9;
      border: none;
      border-radius: 0;
      box-shadow: none;
    }

    @include mq.mq(variables.$mq-5-min) {
      position: absolute;
      top: 0;
      left: helpers.px-to-rem(250);
      min-width: helpers.px-to-rem(200);
    }

    // Nested deeper levels
    .child-menu__child-menu {
      background-color: #fafafa;
      border-left: 2px solid #e0e0e0;
      @include mq.mq(variables.$mq-5-min) {
        left: helpers.px-to-rem(200);
      }
    }

    // Style links in deeper menus
    .child-menu__list-link {
      font-size: 0.9rem;
      padding: .2rem 1.4rem;
      
      &:hover {
        background-color: #d8d8d8;
      }
    }
  }

}
</style>
